import { motion, useInView } from 'framer-motion';
import { useRef } from 'react';
import { Sparkles, Heart, Droplets, Smile, Eye } from 'lucide-react';

export const Phase2Section = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const skills = [
    {
      icon: Sparkles,
      title: "Rétablir l'harmonie énergétique",
      description: "Grâce à ma technique d'étirement des filaments. Tu vas remettre dans son axe tout ce qui a été désaligné et qui peut créer des douleurs, de la fatigue ou une baisse de l'humeur."
    },
    {
      icon: Droplets,
      title: "Encoder des élixirs",
      description: "Tu connais les fleurs de Bach ? Le principe est similaire. Sauf que tu ne vas agir pas uniquement sur le plan émotionnel. Je t'apprends à capturer et transmettre les fréquences harmonieuses des dauphins."
    },
    {
      icon: Heart,
      title: "Soigner dans la joie et le jeu",
      description: "C'est le secret d'un soin énergétique réussi ! Je suis là pour t'aider à avoir confiance en tes propres capacités énergétiques et à maîtriser les protocoles secrets."
    },
    {
      icon: Smile,
      title: "Activer ta \"dolphin banane !\"",
      description: "C'est ta capacité à avoir le sourire en toutes circonstances ! Je te partage une technique de respiration qui active en toi une vague d'énergie vitale."
    },
    {
      icon: Eye,
      title: "Recevoir une guidance Maîtres Dauphins",
      description: "Grâce à l'oracle. Je t'apprends à te connecter à l'énergie des dauphins pour recevoir des messages personnalisés."
    }
  ];

  return (
    <section ref={ref} className="section-padding bg-background relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-accent/30 to-transparent" />

      <div className="container-luxury relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-4xl mx-auto mb-16"
        >
          <span className="text-accent font-body text-sm tracking-[0.3em] uppercase mb-4 block">
            Phase 2
          </span>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl text-foreground leading-tight mb-6">
            Tu canalises les fréquences vibratoires des dauphins pour faire des 
            <span className="text-gradient-gold"> soins énergétiques</span>
          </h2>
          <p className="text-lg text-muted-foreground font-body">
            Tu as maintenant toutes les capacités pour réaliser des auto-soins ou des soins énergétiques 
            sur tes proches ou tes animaux.
          </p>
        </motion.div>

        {/* Image + Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.2, duration: 0.8 }}
            className="relative"
          >
            <div className="absolute inset-0 bg-accent/20 blur-3xl rounded-full" />
            <img
              src="https://gillesdelieuze.com/wp-content/uploads/2024/11/gilles1385_femme_qui_recoit_des_energies_dorees_qui_sintegrent__2848e153-ef08-45da-b5b8-4577668a8f5a.png"
              alt="Soins énergétiques"
              className="relative rounded-3xl w-full border border-accent/20 shadow-gold"
            />
          </motion.div>

          {/* Skills List */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ delay: 0.3, duration: 0.8 }}
          >
            <h3 className="font-display text-2xl text-foreground mb-8">
              Je vais te montrer comment :
            </h3>

            <div className="space-y-6">
              {skills.map((skill, index) => (
                <motion.div
                  key={skill.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + index * 0.1 }}
                  className="flex gap-4 group"
                >
                  <div className="w-12 h-12 rounded-xl bg-accent/10 flex items-center justify-center shrink-0 group-hover:bg-accent/20 transition-colors">
                    <skill.icon className="w-6 h-6 text-accent" />
                  </div>
                  <div>
                    <h4 className="font-display text-lg text-foreground mb-1">{skill.title}</h4>
                    <p className="text-muted-foreground font-body text-sm leading-relaxed">
                      {skill.description}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Phase2Section;
